/**
 * Keybr-style adaptive model over a layout's keys. Each key carries a smoothed
 * time-to-type; confidence = target time ÷ actual time, so 1.0 means the key is at
 * target speed. A new key unlocks only once every included key reaches confidence 1,
 * and the weakest included key becomes the focus the generated text leans on.
 *
 * "Keys" here are the layout's ordering units: English letters, Bijoy key+shift
 * labels — ordered by the frequencies from the research so the most useful keys
 * come first.
 */

const ALPHA = 0.1; // the EMA weight Keybr uses (makeFilter(0.1))
const MIN_INCLUDED = 6;

/** Frequency order (most used first) for each layout's adaptive key sequence. */
export const ADAPTIVE_ORDER = {
    english: 'enitrlsadhcumpgybfwvkxjqz'.split(''),
    // Bijoy ordered by physical-key share (research §2.11), as key characters.
    bijoy: 'fcvgdjbmkhnyswlrtziuoxaep'.split(''),
    // Avro letters by how often the practice words need them (counted from their spellings).
    avro: 'oahirntskebdpuymlgcjzwf'.split(''),
};

/** ms per standardized stroke at a target WPM (5 strokes = 1 word). */
export const msPerStrokeAt = (wpm) => 60000 / (wpm * 5);

export function createKeyModel(layoutId) {
    return { layoutId, order: ADAPTIVE_ORDER[layoutId] || ADAPTIVE_ORDER.english, times: {}, best: {}, samples: {} };
}

/** Feed one lesson's per-key timings in. `samples` = { key: { meanMs, total } }. */
export function updateKeyModel(model, samples) {
    const next = { ...model, times: { ...model.times }, best: { ...model.best }, samples: { ...model.samples } };
    Object.entries(samples).forEach(([key, { meanMs }]) => {
        if (!meanMs || !Number.isFinite(meanMs)) return;
        const prev = next.times[key];
        const filtered = prev == null ? meanMs : prev + ALPHA * (meanMs - prev);
        next.times[key] = filtered;
        next.best[key] = next.best[key] == null ? filtered : Math.min(next.best[key], filtered);
        next.samples[key] = (next.samples[key] || 0) + 1;
    });
    return next;
}

const confidence = (model, key, targetMs, useBest) => {
    const t = useBest ? model.best[key] : model.times[key];
    if (t == null) return 0;
    return targetMs / t;
};

/**
 * Which keys are unlocked and which is the focus, given a target WPM.
 * @returns {{ included: string[], focus: string, confidences: Record<string, number> }}
 */
export function adaptiveState(model, targetWpm, { useBest = true } = {}) {
    const targetMs = msPerStrokeAt(targetWpm);
    const order = model.order;
    const confidences = {};
    order.forEach((key) => { confidences[key] = confidence(model, key, targetMs, useBest); });

    const included = order.slice(0, MIN_INCLUDED);
    for (let i = MIN_INCLUDED; i < order.length; i += 1) {
        const allReady = included.every((key) => confidences[key] >= 1);
        if (!allReady) break;
        included.push(order[i]);
    }
    // Focus = weakest included key still below target; else the newest.
    let focus = included[included.length - 1];
    let worst = Infinity;
    included.forEach((key) => {
        const c = confidences[key];
        if (c < 1 && c < worst) { worst = c; focus = key; }
    });
    return { included, focus, confidences, targetMs };
}

/** Rough overall progress 0..1 across the whole key set. */
export function adaptiveProgress(model, targetWpm) {
    const { confidences } = adaptiveState(model, targetWpm);
    const vals = model.order.map((k) => Math.min(1, confidences[k] || 0));
    return vals.reduce((a, b) => a + b, 0) / vals.length;
}
